import Phaser from 'phaser';
import { TEXT_DEFAULT, TEXT_KEY, TEXT_SIZE, findItem } from '../../config/creator';
import type { CreatorPieceDef } from '../../core/game';
import { colorGroups, colorsHash, recolorPixels } from './recolor';

/**
 * Drawing Level Creator pieces — shared by the editor and GameScene, so a
 * piece looks the same in both. Recoloured art becomes a canvas texture per
 * (key, colours), made once and cached on the game's texture manager.
 * Textboxes are Phaser Text; floating and glowing items move/shine here too
 * (render-only, spec 2026-11-02 §1).
 */

/** An image piece, or a textbox. */
export type PieceObject = Phaser.GameObjects.Image | Phaser.GameObjects.Text;

/** A textbox's one colour group: its words start white. */
export const TEXT_GROUP = '#ffffff';

function pixelsOf(scene: Phaser.Scene, key: string): ImageData | null {
  if (!scene.textures.exists(key)) return null;
  const src = scene.textures.get(key).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  const w = src.width;
  const h = src.height;
  if (!w || !h) return null;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(src, 0, 0);
  try {
    return ctx.getImageData(0, 0, w, h);
  } catch {
    return null; // a tainted canvas — leave the art as it is
  }
}

const groupCache = new Map<string, string[]>();

/** The colour groups of an atlas key (cached per key). A textbox has one. */
export function textureColorGroups(scene: Phaser.Scene, key: string): string[] {
  if (key === TEXT_KEY) return [TEXT_GROUP];
  const hit = groupCache.get(key);
  if (hit) return hit;
  const img = pixelsOf(scene, key);
  const groups = img ? colorGroups(img.data) : [];
  groupCache.set(key, groups);
  return groups;
}

/** The texture key to draw `key` with `colors` applied (the key itself if none). */
export function recoloredTexture(
  scene: Phaser.Scene,
  key: string,
  colors: Readonly<Record<string, string>> | undefined,
): string {
  if (!colors || Object.keys(colors).length === 0) return key;
  const out = `cr-${key}-${colorsHash(colors)}`;
  if (scene.textures.exists(out)) return out;
  const img = pixelsOf(scene, key);
  if (!img) return key;
  const recolored = recolorPixels(img.data, colors);
  const tex = scene.textures.createCanvas(out, img.width, img.height);
  if (!tex) return key;
  const out2 = tex.context.createImageData(img.width, img.height);
  out2.data.set(recolored);
  tex.context.putImageData(out2, 0, 0);
  tex.refresh();
  return out;
}

/** Layer depths shared by the editor and the game: textboxes over everything. */
export const PIECE_DEPTH = { decor: 0.8, top: 1.45, text: 4.9 } as const;

/**
 * Make the object for one piece (no depth set — the caller layers it).
 * `still` skips the motion and the light (a scaled preview in a container).
 */
export function makePieceImage(
  scene: Phaser.Scene,
  p: CreatorPieceDef,
  opts: { still?: boolean } = {},
): PieceObject {
  if (p.key === TEXT_KEY) {
    const t = scene.add
      .text(p.x, p.y, p.text || TEXT_DEFAULT, {
        fontSize: `${TEXT_SIZE}px`,
        fontStyle: 'bold',
        color: p.colors?.[TEXT_GROUP] ?? TEXT_GROUP,
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5);
    sizePiece(t, p);
    return t;
  }
  const tex = scene.textures.exists(p.key) ? recoloredTexture(scene, p.key, p.colors) : '__MISSING';
  const img = scene.add.image(p.x, p.y, tex);
  sizePiece(img, p);
  if (!opts.still) animatePiece(scene, img, p);
  return img;
}

/** Apply a piece's scale (longest side = item size × scale), rotation and flip. */
export function sizePiece(img: PieceObject, p: CreatorPieceDef): void {
  if (p.key === TEXT_KEY) {
    img.setScale(p.scale);
  } else {
    const natural = findItem(p.key)?.size ?? 64;
    const longest = Math.max(img.frame.width, img.frame.height) || 1;
    img.setScale((natural * p.scale) / longest);
  }
  img.setAngle(p.rotation);
  img.setFlipX(!!p.flip);
}

/** Move a piece's object, keeping a floating piece's bob centred on it. */
export function placePiece(obj: PieceObject, x: number, y: number): void {
  obj.setX(x);
  if (obj.getData('floatY') !== undefined) obj.setData('floatY', y);
  else obj.setY(y);
}

const FLOAT_PX = 6;
/** The glow decor's light (generated, black background, drawn ADD). */
const GLOW_KEY = 'fx-cr-glow';
const FLOAT_MS = 1400;

/** Floating pieces bob; glowing ones shine with a light under them. */
function animatePiece(
  scene: Phaser.Scene,
  img: Phaser.GameObjects.Image,
  p: CreatorPieceDef,
): void {
  const it = findItem(p.key);
  if (it?.anim === 'float') {
    img.setData('floatY', p.y);
    const phase = (p.x * 7 + p.y * 13) % FLOAT_MS;
    const tick = (time: number) => {
      if (!img.active) {
        scene.events.off(Phaser.Scenes.Events.UPDATE, tick);
        return;
      }
      const base = img.getData('floatY') as number;
      img.y = base + Math.sin(((time + phase) / FLOAT_MS) * Math.PI * 2) * FLOAT_PX;
    };
    scene.events.on(Phaser.Scenes.Events.UPDATE, tick);
    img.once(Phaser.GameObjects.Events.DESTROY, () =>
      scene.events.off(Phaser.Scenes.Events.UPDATE, tick),
    );
  }
  if (it?.glow !== undefined && scene.textures.exists(GLOW_KEY)) {
    // A generated soft light on black, ADDED under the piece (black adds
    // nothing), tinted the item's colour, pulsing; it follows the piece.
    const halo = scene.add.image(img.x, img.y, GLOW_KEY).setBlendMode(Phaser.BlendModes.ADD);
    halo.setTint(it.glow).setAlpha(0.4).setName('creator-glow');
    const follow = () => {
      if (!img.active) return;
      halo.setPosition(img.x, img.y);
      halo.setDisplaySize(img.displayWidth * 1.9, img.displayWidth * 1.9);
      halo.setDepth(img.depth - 1e-6);
      halo.setVisible(img.visible);
    };
    follow();
    scene.events.on(Phaser.Scenes.Events.UPDATE, follow);
    const pulse = scene.tweens.add({
      targets: halo,
      alpha: 0.75,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    img.once(Phaser.GameObjects.Events.DESTROY, () => {
      scene.events.off(Phaser.Scenes.Events.UPDATE, follow);
      pulse.remove();
      halo.destroy();
    });
  }
}
